// src/pages/AdminDashboardPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import DepartmentAnnouncementEditor from "../components/DepartmentAnnouncementEditor";

import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from "recharts";

/* ------------------------ helpers ------------------------ */
function toISODate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function startOfCalendarGrid(monthDate) {
  const first = startOfMonth(monthDate);
  const day = first.getDay();
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - day);
  return gridStart;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function formatMonthTitle(d) {
  return d.toLocaleString(undefined, { month: "long", year: "numeric" });
}
function formatDateTime(dtStr) {
  const d = new Date(dtStr);
  if (Number.isNaN(d.getTime())) return String(dtStr || "");
  const date = d.toLocaleDateString(undefined, { year: "numeric", month: "2-digit", day: "2-digit" });
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${date} · ${time}`;
}

/* ✅ Step A: safe JSON reader (prevents Unexpected token <) */
async function readJsonSafe(res) {
  if (!res) return null;

  let text = "";
  try {
    text = await res.text();
  } catch {
    return null;
  }

  const t = String(text || "").trim();
  if (!t) return null;

  try {
    return JSON.parse(t);
  } catch {
    return { error: "Non-JSON response", raw: t };
  }
}

/* ------------------------ SIMPLE Calendar UI ------------------------ */
function MiniCalendar({ monthDate, setMonthDate, markedDatesSet, selectedISO, onSelectISO, onToday }) {
  const gridStart = useMemo(() => startOfCalendarGrid(monthDate), [monthDate]);
  const monthStart = useMemo(() => startOfMonth(monthDate), [monthDate]);
  const monthEnd = useMemo(() => endOfMonth(monthDate), [monthDate]);
  const todayISO = useMemo(() => toISODate(new Date()), []);

  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 42; i++) arr.push(addDays(gridStart, i));
    return arr;
  }, [gridStart]);

  const isInMonth = (d) => d >= monthStart && d <= monthEnd;

  return (
    <div className="simp-cal">
      <div className="simp-cal-head">
        <div className="simp-cal-title">Calendar</div>

        <div className="simp-cal-actions">
          <button className="simp-cal-btn" type="button" onClick={onToday}>
            Today
          </button>

          <button
            className="simp-cal-icon"
            type="button"
            onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))}
            aria-label="Prev month"
          >
            ‹
          </button>

          <button
            className="simp-cal-icon"
            type="button"
            onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))}
            aria-label="Next month"
          >
            ›
          </button>
        </div>
      </div>

      <div className="simp-cal-month">{formatMonthTitle(monthDate)}</div>

      <div className="simp-cal-dow">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((x) => (
          <div key={x} className="simp-cal-dow-cell">
            {x}
          </div>
        ))}
      </div>

      <div className="simp-cal-grid">
        {days.map((d) => {
          const iso = toISODate(d);
          const muted = !isInMonth(d);
          const isToday = iso === todayISO;
          const isSelected = iso === selectedISO;
          const hasEvent = markedDatesSet?.has(iso);

          return (
            <button
              key={iso}
              type="button"
              className={["simp-day", muted ? "muted" : "", isToday ? "today" : "", isSelected ? "selected" : ""].join(" ")}
              onClick={() => onSelectISO?.(iso)}
              aria-label={iso}
              title={iso}
            >
              {d.getDate()}
              {hasEvent ? <span className="simp-dot" /> : null}
            </button>
          );
        })}
      </div>

      <div className="simp-cal-footnote">
        Selected: <span>{selectedISO}</span>
      </div>
    </div>
  );
}

/* ------------------------ Page ------------------------ */
function AdminDashboardPage() {
  const { user, authFetch } = useAuth();
  const navigate = useNavigate();

  const [todayData, setTodayData] = useState(null);
  const [todayLoading, setTodayLoading] = useState(true);
  const [todayError, setTodayError] = useState("");

  // ✅ calendar selected date -> table data
  const [selectedData, setSelectedData] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedError, setSelectedError] = useState("");

  const [weekStats, setWeekStats] = useState(null);
  const [weekStatsLoading, setWeekStatsLoading] = useState(true);

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(true);

  const [monthDate, setMonthDate] = useState(() => new Date());
  const [selectedISO, setSelectedISO] = useState(() => toISODate(new Date()));
  const todayISO = useMemo(() => toISODate(new Date()), []);

  const [tableSearch, setTableSearch] = useState("");
  const [tableStatusFilter, setTableStatusFilter] = useState("all");

  useEffect(() => {
    let alive = true;

    async function loadToday() {
      try {
        setTodayLoading(true);
        setTodayError("");

        // ✅ Step A: relative API + safe parsing
        const res = await authFetch("/api/schedules/today", { cache: "no-store" });
        const data = await readJsonSafe(res);

        if (!res.ok) {
          const msg = data?.error || `Failed to load today overview (status ${res.status})`;
          throw new Error(msg);
        }

        if (alive) setTodayData(data);
      } catch (err) {
        if (alive) setTodayError(err.message || "Failed to load today overview");
      } finally {
        if (alive) setTodayLoading(false);
      }
    }

    loadToday();
    return () => {
      alive = false;
    };
  }, [authFetch]);

  useEffect(() => {
    let alive = true;

    async function loadWeekStats() {
      try {
        setWeekStatsLoading(true);

        const weekId = todayData?.week_id;
        const url = weekId
          ? `/api/dashboard/this-week?weekId=${weekId}`
          : "/api/dashboard/this-week";

        const res = await authFetch(url, { cache: "no-store" });
        const data = await readJsonSafe(res);

        if (!res.ok) return;
        if (alive) setWeekStats(data);
      } finally {
        if (alive) setWeekStatsLoading(false);
      }
    }

    loadWeekStats();

    return () => {
      alive = false;
    };
  }, [authFetch, todayData?.week_id]);

  useEffect(() => {
    let alive = true;

    async function loadCharts() {
      try {
        setChartLoading(true);

        const weekId = todayData?.week_id;
        if (!weekId) {
          if (alive) setChartData(null);
          return;
        }

        const res = await authFetch(`/api/dashboard/charts?weekId=${weekId}`, {
          cache: "no-store",
        });
        const data = await readJsonSafe(res);

        if (!res.ok) return;

        if (alive) setChartData(data);
      } finally {
        if (alive) setChartLoading(false);
      }
    }

    loadCharts();

    return () => {
      alive = false;
    };
  }, [authFetch, todayData?.week_id]);

  // ✅ load events for the visible grid
  useEffect(() => {
    let alive = true;

    async function loadEvents() {
      try {
        setEventsLoading(true);

        const gridStart = startOfCalendarGrid(monthDate);
        const from = toISODate(gridStart);
        const to = toISODate(addDays(gridStart, 41));

        const res = await authFetch(`/api/events?from=${from}&to=${to}`, { cache: "no-store" });
        const data = await readJsonSafe(res);

        if (!res.ok) {
          if (alive) setEvents([]);
          return;
        }

        const list = Array.isArray(data) ? data : Array.isArray(data?.events) ? data.events : [];
        if (alive) setEvents(list);
      } finally {
        if (alive) setEventsLoading(false);
      }
    }

    loadEvents();
    return () => {
      alive = false;
    };
  }, [authFetch, monthDate]);

  // ✅ When user clicks a calendar date -> fetch schedule for that date (safe fallback)
  useEffect(() => {
    let alive = true;

    async function loadSelectedDay() {
      if (selectedISO === todayISO) {
        if (alive) {
          setSelectedData(null);
          setSelectedError("");
          setSelectedLoading(false);
        }
        return;
      }

      try {
        setSelectedLoading(true);
        setSelectedError("");

        const candidates = [
          `/api/schedules/by-date?date=${selectedISO}`,
          `/api/schedules/date?date=${selectedISO}`,
          `/api/schedules/day?date=${selectedISO}`,
        ];

        let okData = null;

        for (const url of candidates) {
          const res = await authFetch(url, { cache: "no-store" });
          if (!res.ok) continue;

          const data = await readJsonSafe(res);
          if (data && !data?.error) {
            okData = data;
            break;
          }
        }

        if (!okData) {
          throw new Error("No schedule API found for selected date (backend endpoint not added yet).");
        }

        if (alive) setSelectedData(okData);
      } catch (err) {
        if (alive) {
          setSelectedData({ rows: [] });
          setSelectedError(err.message || "Failed to load selected date schedule.");
        }
      } finally {
        if (alive) setSelectedLoading(false);
      }
    }

    loadSelectedDay();
    return () => {
      alive = false;
    };
  }, [authFetch, selectedISO, todayISO]);

  const todayRows = useMemo(() => {
    const rows = todayData?.rows;
    return Array.isArray(rows) ? rows : [];
  }, [todayData]);

  const tableRows = useMemo(() => {
    if (selectedISO === todayISO) return todayRows;
    const rows = selectedData?.rows;
    return Array.isArray(rows) ? rows : [];
  }, [selectedISO, todayISO, todayRows, selectedData]);

  const filteredTableRows = useMemo(() => {
    const q = String(tableSearch || "").trim().toLowerCase();

    return (tableRows || []).filter((r) => {
      const dept = String(r.department || "").toLowerCase();
      const lead = String(r.lead || "").toLowerCase();
      const start = String(r.start_time || "").toLowerCase();
      const end = String(r.end_time || "").toLowerCase();
      const open = Number(r?.open_duties || 0);
      const status = open > 0 ? "open" : "covered";

      const matchesSearch =
        !q ||
        dept.includes(q) ||
        lead.includes(q) ||
        start.includes(q) ||
        end.includes(q) ||
        status.includes(q);

      const matchesStatus =
        tableStatusFilter === "all" ||
        status === tableStatusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [tableRows, tableSearch, tableStatusFilter]);

  const summary = todayData?.summary || {};
  const weekFromToday = todayData?.weekStats || {};
  const rightStats = weekStats || weekFromToday || {};
  const alerts = Array.isArray(rightStats?.alerts) ? rightStats.alerts : [];

  const markedDatesSet = useMemo(() => {
    const s = new Set();
    for (const e of events || []) {
      const when = e?.start || e?.date || e?.datetime || e?.start_time || e?.when;
      if (!when) continue;
      const d = new Date(when);
      if (Number.isNaN(d.getTime())) continue;
      s.add(toISODate(d));
    }
    return s;
  }, [events]);

  const upcomingEvents = useMemo(() => {
    const now = new Date();
    const list = Array.isArray(events) ? [...events] : [];
    list.sort((a, b) => new Date(a?.start || a?.datetime || a?.date || 0) - new Date(b?.start || b?.datetime || b?.date || 0));
    return list.filter((e) => {
      const when = new Date(e?.start || e?.datetime || e?.date || 0);
      return !Number.isNaN(when.getTime()) && when >= now;
    });
  }, [events]);

  const topEvents = useMemo(() => upcomingEvents.slice(0, 4), [upcomingEvents]);

  const todayCardStats = chartData?.todayCard || {};

  const plannedToday = Number(todayCardStats?.plannedToday || 0);
  const activeToday = Number(todayCardStats?.activeToday || 0);
  const sickToday = Number(todayCardStats?.sickToday || 0);
  const lateToday = Number(todayCardStats?.lateToday || 0);
  const emergencyToday = Number(todayCardStats?.emergencyToday || 0);
  const otherToday = Number(todayCardStats?.otherToday || 0);

  const plannedPct = plannedToday > 0
    ? Math.round((activeToday / plannedToday) * 100)
    : 0;

  const todayDonutData = useMemo(() => {
    if (!chartData) return [];

    return [
      { name: "Active", value: activeToday },
      { name: "Sick", value: sickToday },
      { name: "Late", value: lateToday },
      { name: "Emergency", value: emergencyToday },
      { name: "Other", value: otherToday },
    ].filter((x) => Number(x.value || 0) > 0);
  }, [chartData, activeToday, sickToday, lateToday, emergencyToday, otherToday]);

  const weekDutyData = useMemo(() => {
    if (!chartData) return [];

    const w = chartData?.weekDonut || {};
    const leaveBucket =
      Number(w.dayOff || 0) +
      Number(w.vacation || 0) +
      Number(w.holiday || 0) +
      Number(w.sick || 0);

    return [
      { name: "Shift", value: Number(w.shift || 0) },
      { name: "Training", value: Number(w.training || 0) },
      { name: "Leave", value: leaveBucket },
    ].filter((x) => Number(x.value || 0) > 0);
  }, [chartData]);

  const weekHoursData = useMemo(() => {
    if (!chartData) return [];

    const h = chartData?.weekHours || {};
    const scheduled = Number(h.scheduledMinutes || 0);
    const breakMins = Number(h.breakMinutes || 0);
    const working = Number(h.workingMinutes || 0);

    return [
      { name: "Scheduled", value: scheduled },
      { name: "Break", value: breakMins },
      { name: "Net Work", value: working },
    ].filter((x) => Number(x.value || 0) > 0);
  }, [chartData]);

  const todayChartColors = ["#2563eb", "#ef4444", "#f59e0b", "#8b5cf6", "#06b6d4"];
  const weekDutyColors = ["#2563eb", "#f59e0b", "#22c55e"];
  const weekHoursColors = ["#2563eb", "#f59e0b", "#22c55e"];

  const summaryCards = useMemo(() => {
    return [
      {
        title: "Attendance planned today",
        value: chartLoading ? "…" : `${activeToday} / ${plannedToday}`,
        subValue: chartLoading ? "…" : `${plannedPct}% active`,
        accent: "blue",
        icon: "👥",
        path: "/admin/attendance-today",
      },
      {
        title: "Staff scheduled today",
        value: todayLoading ? "…" : String(summary?.staffScheduledToday ?? "—"),
        subValue: "Departments covered",
        accent: "cyan",
        icon: "☁",
        path: "/admin/schedule",
      },
      {
        title: "Open duties",
        value: todayLoading ? "…" : String(summary?.openDuties ?? "—"),
        subValue: "Needs attention",
        accent: "amber",
        icon: "🧾",
        path: "/admin/schedule",
      },
      {
        title: "Next sort start",
        value: todayLoading ? "…" : String(summary?.nextSortStart ?? "—"),
        subValue: "Earliest shift",
        accent: "green",
        icon: "✅",
        path: "/admin/schedule",
      },
    ];
  }, [
    chartLoading,
    activeToday,
    plannedToday,
    plannedPct,
    todayLoading,
    summary,
  ]);

  function clearTableFilters() {
    setTableSearch("");
    setTableStatusFilter("all");
  }

  const hasActiveTableFilters =
    tableSearch.trim() !== "" || tableStatusFilter !== "all";

  const displayName = user?.name || user?.username || "Admin User";

  const roleKey = String(user?.role || "").toLowerCase();
  const canManageAnnouncement =
  roleKey === "superadmin" || roleKey === "manager";

  const initials = String(displayName)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0]?.toUpperCase())
    .join("");

  return (
    <div className="ref-ui">
      <style>{`
        :root{
          --app-header-h: 0px;
          --bg: #f3f6fb;
          --card: #ffffff;
          --text: #0f172a;
          --muted: #64748b;
          --line: rgba(2, 6, 23, .08);
          --shadow: 0 10px 22px rgba(2, 6, 23, .06);
          --radius: 12px;

          --blue: #2563eb;
          --amber: #f59e0b;
          --cyan: #06b6d4;
          --green: #22c55e;
        }

        html, body { height: 100%; }

        .ref-ui,
        .ref-ui *{
          box-sizing: border-box;
        }

        .ref-ui{
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
          background: var(--bg);
          min-height: 100%;
        }

        .ref-ui .dashboard-main,
        .ref-ui .ref-stats,
        .ref-ui .ref-content,
        .ref-ui .ref-bottom{
          width: 100%;
          max-width: 100%;
          min-width: 0;
        }

        .ref-ui .dashboard-main{
          height: 100%;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          display: grid;
          grid-template-rows: auto auto minmax(0, 1fr);
          padding: 18px 22px 22px;
          gap: 14px;
          scrollbar-width: none;
        }
        .ref-ui .dashboard-main::-webkit-scrollbar{ width: 0; height: 0; }

        .ref-topbar{
          display:flex;
          align-items:center;
          justify-content: space-between;
          gap: 16px;
        }
        .ref-hello{
          font-size: 26px;
          font-weight: 950;
          color: var(--text);
          margin: 0;
        }

        .ref-top-actions{
          display:flex;
          align-items:center;
          gap: 10px;
        }
        .ref-action-icon{
          border: 1px solid var(--line);
          background: #fff;
          height: 38px;
          width: 38px;
          border-radius: 10px;
          box-shadow: var(--shadow);
          cursor: pointer;
          font-weight: 950;
          color: var(--text);
          transition: transform .15s ease, box-shadow .15s ease;
        }
        .ref-action-icon:hover{ transform: translateY(-1px); box-shadow: 0 14px 26px rgba(2,6,23,.08); }

        .ref-avatar{
          height: 38px;
          width: 38px;
          border-radius: 999px;
          background: rgba(37, 99, 235, .12);
          color: var(--blue);
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight: 950;
          border: 1px solid rgba(37, 99, 235, .18);
        }

        .ref-stats{
          display:grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 18px;
        }
        .ref-stat{
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: var(--radius);
          box-shadow: var(--shadow);
          padding: 16px 16px;
          display:flex;
          min-width: 0;
          align-items:center;
          justify-content: space-between;
          min-height: 78px;
          transition: transform .18s ease, box-shadow .18s ease;
          cursor: pointer;
        }
        .ref-stat:hover{
          transform: translateY(-2px);
          box-shadow: 0 18px 28px rgba(2, 6, 23, .09);
        }

        .ref-stat-arrow{
          font-weight: 900;
          color: var(--blue);
          opacity: 0;
          transform: translateX(-4px);
          transition: opacity .18s ease, transform .18s ease;
        }

        .ref-stat:hover .ref-stat-arrow{
          opacity: 1;
          transform: translateX(0);
        }

        .ref-stat-left{
          display:flex;
          align-items:center;
          gap: 12px;
          min-width: 0;
        }
        .ref-stat-left > div{ min-width: 0; }
        .ref-stat-icon{
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight: 950;
          color: var(--text);
        }

        .ref-stat-title{
          font-weight: 950;
          font-size: 13px;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ref-stat-value{
          margin: 6px 0 0;
          font-size: 20px;
          font-weight: 950;
          color: var(--text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }

        .ref-stat.blue .ref-stat-title{ color: var(--blue); }
        .ref-stat.amber .ref-stat-title{ color: var(--amber); }
        .ref-stat.cyan .ref-stat-title{ color: var(--cyan); }
        .ref-stat.green .ref-stat-title{ color: var(--green); }

        .ref-stat.blue .ref-stat-icon{ background: rgba(37,99,235,.10); color: var(--blue); }
        .ref-stat.amber .ref-stat-icon{ background: rgba(245,158,11,.12); color: var(--amber); }
        .ref-stat.cyan .ref-stat-icon{ background: rgba(6,182,212,.12); color: var(--cyan); }
        .ref-stat.green .ref-stat-icon{ background: rgba(34,197,94,.12); color: var(--green); }

        .ref-content{
          min-height: 0;
          display: grid;
          grid-template-rows: auto auto auto;
          gap: 16px;
        }
        .ref-panel{
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: var(--radius);
          box-shadow: var(--shadow);
          min-height: 0;
          overflow: hidden;
          display:flex;
          flex-direction: column;
        }

        .ref-panel-head{
          padding: 16px 16px 12px;
          display:flex;
          align-items:center;
          justify-content: space-between;
          gap: 12px;
        }

        .ref-filterbar{
          display:flex;
          align-items:end;
          gap:10px;
          flex-wrap:wrap;
        }

        .ref-filteritem{
          display:flex;
          flex-direction:column;
          gap:6px;
          min-width:140px;
        }

        .ref-filteritem label{
          font-size:11px;
          color:#64748b;
          font-weight:900;
          text-transform:uppercase;
          letter-spacing:.04em;
        }

        .ref-filterbar input,
        .ref-filterbar select{
          height:36px;
          border-radius:10px;
          border:1px solid rgba(2,6,23,.10);
          background:#fff;
          padding:0 12px;
          font-weight:800;
          color:#0f172a;
          font-size:12px;
          outline:none;
        }

        .ref-filterbar input:focus,
        .ref-filterbar select:focus{
          border-color:#93c5fd;
          box-shadow: 0 0 0 3px rgba(59,130,246,.14);
        }

        .ref-clear-btn{
          height:36px;
          border-radius:10px;
          border:1px solid rgba(2,6,23,.10);
          background:#fff;
          padding:0 12px;
          font-weight:900;
          color:#0f172a;
          cursor:pointer;
          box-shadow: var(--shadow);
          transition: transform .15s ease, box-shadow .15s ease;
        }

        .ref-clear-btn:hover{
          transform: translateY(-1px);
          box-shadow: 0 14px 26px rgba(2,6,23,.08);
        }
        .ref-panel-title{
          margin: 0;
          font-size: 18px;
          font-weight: 950;
          color: var(--text);
        }

        .ref-table-wrap{
          padding: 0 16px 16px;
          min-height: 0;
          flex: 1;
          overflow: auto;
          scrollbar-width: none;
        }
        .ref-table-wrap::-webkit-scrollbar{ width: 0; height: 0; }

        .ref-ui .schedule-mini-table{
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }
        .ref-ui .schedule-mini-table thead th{
          text-align: left;
          font-size: 12px;
          color: var(--muted);
          font-weight: 950;
          padding: 12px 10px;
          border-bottom: 1px solid var(--line);
          background: #fff;
          position: sticky;
          top: 0;
          z-index: 1;
        }
        .ref-ui .schedule-mini-table tbody td{
          padding: 12px 10px;
          border-bottom: 1px solid rgba(2, 6, 23, .06);
          color: var(--text);
          font-weight: 700;
        }

        .ref-ui .badge{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          padding: 5px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 950;
          background: rgba(34, 197, 94, .10);
          color: #15803d;
          border: 1px solid rgba(34, 197, 94, .16);
        }

        .ref-bottom{
          min-height: 0;
          display:grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 18px;
          align-items: stretch;
        }

        .ref-bottom--charts .ref-panel{
          min-height: 340px;
        }

        .ref-mini-body{
          padding: 0 16px 16px;
          min-height: 0;
          flex: 1;
          overflow: auto;
          scrollbar-width: none;
        }
        .ref-mini-body::-webkit-scrollbar{ width: 0; height: 0; }

        .ref-chart-body{
          min-height: 280px;
          height: 280px;
          overflow: hidden !important;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        @media (max-width: 520px){
          .ref-chart-body{
            min-height: 240px;
            height: 240px;
          }
        }

        .ref-mini-head{
          padding: 16px 16px 12px;
          display:flex;
          align-items:center;
          justify-content: space-between;
          gap: 10px;
        }
        .ref-mini-title{
          margin: 0;
          font-weight: 950;
          font-size: 16px;
          color: var(--text);
        }
        .ref-viewall{
          border: none;
          background: rgba(37, 99, 235, .10);
          color: var(--blue);
          font-weight: 950;
          padding: 9px 14px;
          border-radius: 999px;
          cursor: pointer;
          transition: transform .15s ease;
        }
        .ref-viewall:hover{ transform: translateY(-1px); }

        .simp-cal{
          height: 100%;
          padding: 16px 16px 12px;
          display:flex;
          flex-direction: column;
          min-height: 0;
        }

        .simp-cal-head{
          display:flex;
          align-items:center;
          justify-content: space-between;
          gap: 10px;
        }

        .simp-cal-title{
          font-weight: 950;
          font-size: 16px;
          color: var(--text);
        }

        .simp-cal-actions{
          display:flex;
          align-items:center;
          gap: 8px;
        }

        .simp-cal-btn{
          border: none;
          background: rgba(37, 99, 235, .10);
          color: var(--blue);
          font-weight: 950;
          padding: 8px 12px;
          border-radius: 999px;
          cursor: pointer;
          transition: transform .15s ease;
        }
        .simp-cal-btn:hover{ transform: translateY(-1px); }

        .simp-cal-icon{
          border: 1px solid var(--line);
          background: #fff;
          width: 36px;
          height: 36px;
          border-radius: 999px;
          cursor: pointer;
          font-weight: 950;
          box-shadow: var(--shadow);
          transition: transform .15s ease;
        }
        .simp-cal-icon:hover{ transform: translateY(-1px); }

        .simp-cal-month{
          margin-top: 10px;
          font-weight: 950;
          color: var(--text);
          font-size: 15px;
        }

        .simp-cal-dow{
          margin-top: 12px;
          display:grid;
          grid-template-columns: repeat(7, minmax(0, 1fr));
          gap: 8px;
        }

        .simp-cal-dow-cell{
          text-align:center;
          color: var(--muted);
          font-size: 11px;
          font-weight: 950;
        }

        .simp-cal-grid{
          margin-top: 10px;
          display: grid;
          grid-template-columns: repeat(7, minmax(0, 1fr));
          grid-template-rows: repeat(6, 1fr);
          gap: 8px;
          flex: 1;
          min-height: 0;
        }

        @keyframes pop {
          0% { transform: scale(.98); }
          60% { transform: scale(1.03); }
          100% { transform: scale(1); }
        }

        .simp-day{
          height: auto;
          width: 100%;
          border: none;
          background: transparent;
          border-radius: 10px;
          cursor: pointer;
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight: 900;
          font-size: 12px;
          color: var(--text);
          position: relative;
          transition: transform .12s ease, background .12s ease, box-shadow .12s ease;
        }

        .simp-day:hover{
          background: rgba(37, 99, 235, .07);
          transform: translateY(-1px);
        }

        .simp-day:active{
          transform: translateY(0px) scale(.98);
        }

        .simp-day.muted{
          color: rgba(100, 116, 139, .55);
        }

        .simp-day.today{
          outline: 2px solid rgba(37, 99, 235, .18);
          background: rgba(37, 99, 235, .05);
        }

        .simp-day.selected{
          background: var(--blue);
          color: #fff;
          box-shadow: 0 10px 18px rgba(37,99,235,.25);
          animation: pop .18s ease;
        }

        .simp-dot{
          position:absolute;
          bottom: 6px;
          width: 6px;
          height: 6px;
          border-radius: 999px;
          background: var(--blue);
        }

        .simp-day.selected .simp-dot{
          background: rgba(255,255,255,.95);
        }

        .simp-cal-footnote{
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid rgba(2,6,23,.06);
          color: var(--muted);
          font-size: 12px;
          font-weight: 800;
        }
        .simp-cal-footnote span{
          color: var(--text);
          font-weight: 950;
        }

        .ref-event{
          display:flex;
          gap: 12px;
          padding: 12px 0;
          border-bottom: 1px solid rgba(2, 6, 23, .06);
        }
        .ref-event:last-child{ border-bottom: none; }
        .ref-event-date{
          width: 46px;
          flex: 0 0 46px;
          text-align:center;
          font-weight: 950;
          color: var(--blue);
        }
        .ref-event-date small{
          display:block;
          font-weight: 950;
          color: var(--muted);
          margin-top: 2px;
          font-size: 11px;
        }
        .ref-event-main{ min-width: 0; }
        .ref-event-title{
          font-weight: 950;
          color: var(--text);
          font-size: 13px;
        }
        .ref-event-sub{
          margin-top: 4px;
          color: var(--muted);
          font-size: 12px;
          font-weight: 700;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }

        .ref-leave{
          display:flex;
          gap: 12px;
          padding: 12px 0;
          border-bottom: 1px solid rgba(2, 6, 23, .06);
          align-items: flex-start;
        }
        .ref-leave:last-child{ border-bottom: none; }
        .ref-leave-ico{
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: rgba(37, 99, 235, .10);
          display:flex;
          align-items:center;
          justify-content:center;
          color: var(--blue);
          font-weight: 950;
          flex: 0 0 36px;
        }
        .ref-leave-title{
          font-weight: 950;
          color: var(--text);
          font-size: 13px;
        }
        .ref-leave-sub{
          margin-top: 4px;
          color: var(--muted);
          font-size: 12px;
          font-weight: 700;
        }

        @media (max-width: 1099px){
          body{ overflow-x: hidden; overflow-y: auto; }
          .ref-ui{ height: auto; overflow-x: hidden; }

          .ref-ui .dashboard-main{
            height: auto;
            overflow: visible;
            padding: 14px 12px 18px;
            grid-template-rows: auto auto auto;
          }

          .ref-stats{ grid-template-columns: 1fr 1fr; }
          .ref-content{ grid-template-rows: auto auto auto; }
          .ref-bottom{ grid-template-columns: 1fr; }

          .ref-content,
          .ref-bottom,
          .ref-stats,
          .ref-panel,
          .ref-panel-head,
          .ref-mini-body,
          .ref-table-wrap{
            min-width: 0;
            width: 100%;
            max-width: 100%;
          }

          .ref-top-actions-wrap{
            flex-wrap: wrap;
            justify-content: flex-end;
          }

          .ref-login-pill{
            display: none;
          }

          .ref-panel-head{
            flex-wrap: wrap;
            gap: 10px;
          }

          .ref-filterbar{
            flex-wrap: wrap;
            justify-content: flex-end;
            width: 100%;
            min-width: 0;
          }

          .ref-filterbar input,
          .ref-filterbar select{
            min-width: 0;
            max-width: 100%;
          }
        }
          .ref-filterbar{ min-width: 0; }
          .ref-filterbar input,
          .ref-filterbar select{
            min-width: 0;
            max-width: 100%;
          }
        }

        @media (max-width: 520px){
          .ref-panel--cal{ overflow: visible; }

          .ref-ui .dashboard-main{
            padding: 12px 10px 18px;
          }

          .ref-panel,
          .ref-content,
          .ref-bottom,
          .ref-stats{
            width: 100%;
            max-width: 100%;
            min-width: 0;
          }

          .ref-panel-head{
            padding: 14px 12px 10px;
          }

          .ref-mini-body,
          .ref-table-wrap{
            padding-left: 12px;
            padding-right: 12px;
          }

          .simp-cal{ padding: 12px 12px 10px; }
          .simp-cal-dow{ gap: 6px; }
          .simp-cal-grid{ gap: 6px; }

          .simp-cal-dow-cell{ font-size: 10px; }
          .simp-day{ font-size: 11px; min-width: 0; }

          .ref-stats{ grid-template-columns: 1fr; }
        }

        @media (max-width: 390px){
          .simp-cal{ padding: 10px 10px 8px; }
          .simp-cal-dow{ gap: 4px; }
          .simp-cal-grid{ gap: 4px; }
        }
      `}</style>

      <main className="dashboard-main">
        <div className="ref-topbar">
          <div>
            <h1 className="ref-hello">Dashboard</h1>
          </div>

          <div className="ref-top-actions ref-top-actions-wrap">
            <div
              className="ref-login-pill"
              style={{
                padding: "10px 14px",
                borderRadius: 999,
                border: "1px solid rgba(2,6,23,.10)",
                background: "#fff",
                fontWeight: 900,
                fontSize: 13,
                color: "#0f172a",
                boxShadow: "0 10px 22px rgba(2,6,23,.06)",
                whiteSpace: "nowrap",
              }}
            >
              Logged in as <span style={{ color: "#2563eb" }}>{displayName}</span>
            </div>

            <button type="button" className="ref-action-icon" title="New Week Schedule" onClick={() => navigate("/admin/schedule")}>
              ＋
            </button>

            <button type="button" className="ref-action-icon" title="Notifications" onClick={() => alert("Notifications later")}>
              🔔
            </button>

            <div className="ref-avatar" title={displayName}>
              {initials || "A"}
            </div>
          </div>
        </div>

        {todayError ? (
          <div style={{ color: "#b91c1c", fontSize: 13, fontWeight: 800 }}>{todayError}</div>
        ) : null}

        <section className="ref-stats">
          {summaryCards.map((c, i) => (
            <div
              className={`ref-stat ${c.accent}`}
              key={i}
              role="button"
              tabIndex={0}
              title={`Open ${c.title}`}
              onClick={() => c.path && navigate(c.path)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (c.path) navigate(c.path);
                }
              }}
            >
              <div className="ref-stat-left">
                <div className="ref-stat-icon">{c.icon}</div>
                <div style={{ minWidth: 0 }}>
                  <p className="ref-stat-title">{c.title}</p>
                  <p className="ref-stat-value">{c.value}</p>
                  {c.subValue ? (
                    <p
                      style={{
                        margin: "4px 0 0",
                        fontSize: 12,
                        fontWeight: 900,
                        color: "#64748b",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {c.subValue}
                    </p>
                  ) : null}
                </div>
              </div>

              <span className="ref-stat-arrow">→</span>
            </div>
          ))}
        </section>

        <section className="ref-content">
          {canManageAnnouncement ? (
            <DepartmentAnnouncementEditor />
          ) : null}

          <div className="ref-panel ref-panel--cal">
            <div className="ref-panel-head">
              <h2 className="ref-panel-title">Today&apos;s Sort Overview</h2>

              <div className="ref-filterbar">
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 900,
                    color: "#64748b",
                    paddingRight: 8,
                    alignSelf: "center",
                  }}
                  title="Table is filtered by selected calendar date"
                >
                  Date: <span style={{ color: "#0f172a" }}>{selectedISO}</span>
                </div>

                <div className="ref-filteritem">
                  <label>Search</label>
                  <input
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                    placeholder="Search dept, lead, time..."
                    style={{ width: "min(220px, 46vw)" }}
                  />
                </div>

                <div className="ref-filteritem">
                  <label>Status</label>
                  <select
                    value={tableStatusFilter}
                    onChange={(e) => setTableStatusFilter(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="covered">Covered</option>
                    <option value="open">Open</option>
                  </select>
                </div>

                {hasActiveTableFilters && (
                  <div className="ref-filteritem">
                    <label>&nbsp;</label>
                    <button
                      type="button"
                      className="ref-clear-btn"
                      onClick={clearTableFilters}
                    >
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="ref-table-wrap">
              {selectedISO !== todayISO && selectedError ? (
                <div
                  style={{
                    padding: 12,
                    borderRadius: 12,
                    background: "rgba(245,158,11,.10)",
                    border: "1px solid rgba(245,158,11,.18)",
                    color: "#92400e",
                    fontWeight: 900,
                    fontSize: 13,
                    marginBottom: 12,
                  }}
                >
                  {selectedError}
                  <div style={{ marginTop: 6, fontWeight: 800, fontSize: 12, color: "#a16207" }}>
                    Tip: Add a backend endpoint like <code>/api/schedules/by-date?date=YYYY-MM-DD</code>
                  </div>
                </div>
              ) : null}

              <table className="schedule-mini-table">
                <thead>
                  <tr>
                    <th>Department</th>
                    <th>Lead</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {selectedISO === todayISO && todayLoading ? (
                    <tr>
                      <td colSpan="5" style={{ padding: 14, color: "#64748b", fontWeight: 900 }}>
                        Loading today overview…
                      </td>
                    </tr>
                  ) : selectedISO !== todayISO && selectedLoading ? (
                    <tr>
                      <td colSpan="5" style={{ padding: 14, color: "#64748b", fontWeight: 900 }}>
                        Loading schedule for {selectedISO}…
                      </td>
                    </tr>
                  ) : filteredTableRows.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ padding: 14, color: "#64748b", fontWeight: 900 }}>
                        No schedule found for {selectedISO}.
                      </td>
                    </tr>
                  ) : (
                    filteredTableRows.map((r, idx) => {
                      const open = Number(r?.open_duties || 0);
                      return (
                        <tr key={idx}>
                          <td style={{ color: "#2563eb", fontWeight: 950 }}>{r.department || "—"}</td>
                          <td style={{ color: "rgba(15,23,42,.72)", fontWeight: 800 }}>{r.lead || "—"}</td>
                          <td style={{ color: "rgba(15,23,42,.72)", fontWeight: 800 }}>{r.start_time || "—"}</td>
                          <td style={{ color: "rgba(15,23,42,.72)", fontWeight: 800 }}>{r.end_time || "—"}</td>
                          <td>
                            {open > 0 ? (
                              <span
                                className="badge"
                                style={{
                                  background: "rgba(245, 158, 11, .14)",
                                  color: "#92400e",
                                  border: "1px solid rgba(245, 158, 11, .22)",
                                }}
                              >
                                {open} open
                              </span>
                            ) : (
                              <span className="badge">Covered</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="ref-bottom ref-bottom--charts">
            {/* TODAY WORKFORCE DONUT */}
            <div className="ref-panel">
              <div className="ref-mini-head">
                <h3 className="ref-mini-title">Today Workforce</h3>
              </div>

              <div className="ref-mini-body ref-chart-body">
                {chartLoading ? (
                  <div style={{ color: "#64748b", fontWeight: 900, fontSize: 13 }}>
                    Loading chart…
                  </div>
                ) : todayDonutData.length ? (
                  <>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                      <Pie
                        data={todayDonutData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={58}
                        outerRadius={88}
                        labelLine={false}
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      >

                          {todayDonutData.map((entry, index) => (
                            <Cell key={index} fill={todayChartColors[index % todayChartColors.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>

                    <div
                      style={{
                        textAlign: "center",
                        marginTop: -6,
                        fontWeight: 900,
                        color: "#64748b",
                        fontSize: 12,
                      }}
                    >
                      Active {activeToday} of {plannedToday} planned
                    </div>
                  </>
                ) : (
                  <div style={{ color: "#64748b", fontWeight: 900, fontSize: 13 }}>
                    No chart data available.
                  </div>
                )}
              </div>
            </div>

            {/* WEEK OVERVIEW DONUT */}
            <div className="ref-panel">
              <div className="ref-mini-head">
                <h3 className="ref-mini-title">Week Overview</h3>
              </div>

              <div className="ref-mini-body ref-chart-body">
                {chartLoading ? (
                  <div style={{ color: "#64748b", fontWeight: 900, fontSize: 13 }}>
                    Loading chart…
                  </div>
                ) : weekDutyData.length ? (
                 <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                    <Pie
                      data={weekDutyData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={58}
                      outerRadius={88}
                      labelLine={false}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    >

                        {weekDutyData.map((entry, index) => (
                          <Cell key={index} fill={weekDutyColors[index % weekDutyColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ color: "#64748b", fontWeight: 900, fontSize: 13 }}>
                    No chart data available.
                  </div>
                )}
              </div>
            </div>

            {/* WEEK HOURS DONUT */}
            <div className="ref-panel">
              <div className="ref-mini-head">
                <h3 className="ref-mini-title">Weekly Hours</h3>
              </div>

              <div className="ref-mini-body ref-chart-body">
                {chartLoading ? (
                  <div style={{ color: "#64748b", fontWeight: 900, fontSize: 13 }}>
                    Loading chart…
                  </div>
                ) : weekHoursData.length ? (
                 <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                    <Pie
                      data={weekHoursData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={58}
                      outerRadius={88}
                      labelLine={false}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    >
                        {weekHoursData.map((entry, index) => (
                          <Cell key={index} fill={weekHoursColors[index % weekHoursColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => {
                          const mins = Number(value || 0);
                          const hrs = (mins / 60).toFixed(1);
                          return [`${hrs} hrs`, "Value"];
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ color: "#64748b", fontWeight: 900, fontSize: 13 }}>
                    No chart data available.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="ref-bottom">
            {/* CALENDAR */}
            <div className="ref-panel">
              <MiniCalendar
                monthDate={monthDate}
                setMonthDate={setMonthDate}
                markedDatesSet={markedDatesSet}
                selectedISO={selectedISO}
                onSelectISO={(iso) => setSelectedISO(iso)}
                onToday={() => {
                  const iso = toISODate(new Date());
                  setSelectedISO(iso);
                  setMonthDate(new Date());
                }}
              />
            </div>

            {/* UPCOMING EVENTS */}
            <div className="ref-panel">
              <div className="ref-mini-head">
                <h3 className="ref-mini-title">Upcoming Events</h3>
                <button
                  className="ref-viewall"
                  type="button"
                  onClick={() => alert("Events page will be added next")}
                >
                  View All
                </button>
              </div>

              <div className="ref-mini-body">
                {eventsLoading ? (
                  <div style={{ color: "#64748b", fontWeight: 900, fontSize: 13 }}>
                    Loading events…
                  </div>
                ) : topEvents.length ? (
                  topEvents.map((e, idx) => {
                    const title = e?.title || e?.name || "Event";
                    const when = new Date(
                      e?.start || e?.datetime || e?.date || e?.start_time || Date.now()
                    );
                    const day = Number.isNaN(when.getTime())
                      ? "—"
                      : String(when.getDate()).padStart(2, "0");
                    const mon = Number.isNaN(when.getTime())
                      ? ""
                      : when.toLocaleString(undefined, { month: "short" });

                    return (
                      <div className="ref-event" key={idx}>
                        <div className="ref-event-date">
                          {day}
                          <small>{mon}</small>
                        </div>

                        <div className="ref-event-main">
                          <div className="ref-event-title">{title}</div>
                          <div className="ref-event-sub">
                            {formatDateTime(
                              e?.start || e?.datetime || e?.date || e?.start_time
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ color: "#64748b", fontWeight: 900, fontSize: 13 }}>
                    No upcoming events.
                  </div>
                )}
              </div>
            </div>

            {/* LEAVES / ALERTS */}
            <div className="ref-panel">
              <div className="ref-mini-head">
                <h3 className="ref-mini-title">Leaves & Alerts</h3>
                <button
                  className="ref-viewall"
                  type="button"
                  onClick={() => alert("Leaves page will be added next")}
                >
                  View All
                </button>
              </div>

              <div className="ref-mini-body">
                <div className="ref-leave">
                  <div className="ref-leave-ico">🗓</div>
                  <div>
                    <div className="ref-leave-title">Total leaves this week</div>
                    <div className="ref-leave-sub">
                      {weekStatsLoading ? "…" : String(rightStats?.vacationDays ?? "—")}
                    </div>
                  </div>
                </div>

                <div className="ref-leave">
                  <div className="ref-leave-ico">🎓</div>
                  <div>
                    <div className="ref-leave-title">Training shifts (week)</div>
                    <div className="ref-leave-sub">
                      {weekStatsLoading ? "…" : String(rightStats?.trainingShifts ?? "—")}
                    </div>
                  </div>
                </div>

                <div className="ref-leave">
                  <div className="ref-leave-ico">⚠</div>
                  <div>
                    <div className="ref-leave-title">Open duties (week)</div>
                    <div className="ref-leave-sub">
                      {weekStatsLoading ? "…" : String(rightStats?.openDuties ?? "—")}
                    </div>
                  </div>
                </div>

                {alerts?.length ? (
                  <>
                    <div
                      style={{
                        marginTop: 10,
                        color: "#0f172a",
                        fontWeight: 950,
                        fontSize: 13,
                      }}
                    >
                      Alerts
                    </div>

                    <div style={{ marginTop: 8 }}>
                      {alerts.slice(0, 3).map((a, idx) => (
                        <div
                          key={idx}
                          style={{
                            marginBottom: 8,
                            border: "1px solid rgba(239, 68, 68, .16)",
                            background: "rgba(239, 68, 68, .06)",
                            padding: "10px 12px",
                            borderRadius: 12,
                            color: "#991b1b",
                            fontWeight: 900,
                            fontSize: 12,
                          }}
                        >
                          {a?.message || String(a)}
                        </div>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default AdminDashboardPage;